<template>
  <div class="meeting-step generic-border">
    <DraggingIndication />
    <div class="meeting-step__details">
      <div class="v-centerer meeting-step__details__basics">
        <EditableLabel
          v-model="value.name"
          :legend="$t('message.STEP_NB', { 'stepNumber': stepNb })"
          :placeholder="$t('message.STEP_NB', { 'stepNumber': stepNb })"
          use-textarea
          @input="$emit('input', value)"
        />
        <EditableLabel
          v-model="value.info"
          :legend="$t('message.DESCRIPTION')"
          :placeholder="$t('message.DESCRIPTION')"
          use-textarea
          @input="$emit('input', value)"
        />
      </div>
      <PagePicker
        v-model="value"
        :see-page="!fromConfig"
        :no-meeting-pages="noMeetingPages"
        :from-config="fromConfig"
        @input="$emit('input', value)"
      />
    </div>
    <div class="meeting-step__duration">
      <div class="meeting-step__duration__content">
        <div class="legend">
          {{ $t('message.DURATION') }}
        </div>
        <div v-if="screenSaver" class="field-hint meeting-step__duration__hint">
          {{ minimumTimeHint }}
        </div>
        <VueTimepicker
          ref="timePicker"
          :key="timePickerKey"
          v-model="timeValue"
          v-click-outside="rmkTimepicker"
          v-bind="timePickerProps"
          format="HH:mm:ss"
          @input="durationUpdated"
          @open="tpOpened"
        />
      </div>
      <button
        class="generic-border generic-border--text-btn generic-border--clickable generic-border--red"
        @click="confirmDelete"
      >
        <Icon>{{ deleteMedium }}</Icon>
        <span>{{ $t('message.DELETE') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Meeting step configuration in settings
 *
 * @vue-prop {Object} value - Meeting step data
 * @vue-prop {Number} index - Index/order of the step in his current list
 * @vue-prop {Boolean} fromConfig - True if editing a meeting template
 *
 * @vue-event {Object} input - Emit 'value' (Meeting step data) when data is edited
 * @vue-event removeStep - Emitted when step request its own deletion
 */

// Import components
import VueTimepicker from 'vue2-timepicker';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import PagePicker from '@/components/page-picker.vue';
// Mixin
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
// Utils
import { calert } from '@/utils/main';
import { deleteMedium } from '@/assets/icons/';

export default {

  name: 'MeetingStep',

  components: {
    DraggingIndication,
    EditableLabel,
    Icon,
    PagePicker,
    VueTimepicker,
  },

  mixins: [mixinMeetingPages],

  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    noMeetingPages: Boolean,
    screenSaver: Boolean,
    fromConfig: Boolean,
  },

  data() {
    return {
      deleteMedium,
      timePickerKey: 1,
      timeValue: {
        HH: '00',
        mm: '01',
        ss: '00',
      },
      tpHasBeenOpened: false,
    };
  },

  computed: {
    stepNb() {
      return this.index + 1;
    },
    /** Array: return an array with seconds user can select */
    limitsTimePickerSeconds() {
      const seconds = [0, 10, 20, 30, 40, 50];
      if (this.valueDuration.asMinutes() >= 1 || this.valueDuration.asHours() >= 1) {
        return seconds;
      }
      return seconds.slice(3);
    },
    /** Array: return an array with minutes user can select */
    limitsTimePickerMinutes() {
      const minutes = [...Array(60).keys()];
      if (this.valueDuration.hours() > 0) {
        return minutes;
      }
      if (this.valueDuration.seconds() < 30) {
        return minutes.slice(1);
      }
      return minutes;
    },
    /** Array: return an array with hours user can select  */
    limitsTimePickerHours() {
      const hours = [...Array(24).keys()];
      if (this.valueDuration.minutes() > 0) {
        return hours;
      }
      if (this.valueDuration.seconds() < 30) {
        return hours.slice(1);
      }
      return hours;
    },
    /** Object: return the current duration of a step */
    valueDuration() {
      return this.$moment.duration(Math.floor(this.value.duration), 's');
    },
    /**
     * Object: return the props depending if the component it used for
     * a screen-saver
     */
    timePickerProps() {
      if (!this.screenSaver) {
        return { secondInterval: 5 };
      }
      return {
        secondRange: this.limitsTimePickerSeconds,
        minuteRange: this.limitsTimePickerMinutes,
        hourRange: this.limitsTimePickerHours,
        secondInterval: 10,
      };
    },
    /** String: return an hint to indicate the minimum time duration */
    minimumTimeHint() {
      return this.$t(
        'message.STEP_MIN_DURATION',
        { stepDuration: this.$tc('message.N_SECONDS', 30, { nb: 30 }) },
      );
    },
  },

  watch: {
    value: {
      handler() {
        this.updateValues();
      },
      deep: true,
    },
  },

  created() {
    this.updateValues();
    if (typeof this.value.info === 'undefined') {
      this.value.info = '';
    }
    this.value.step = this.stepNb;
    this.value.default = this.$t('message.STEP_NB', { stepNumber: this.stepNb });
  },

  methods: {
    rmkTimepicker() {
      this.timePickerKey++;
    },
    tpOpened() {
      this.tpHasBeenOpened = true;
    },
    durationUpdated(val) {
      if (this.tpHasBeenOpened) {
        this.value.duration = Number(val.HH) * 3600 + Number(val.mm) * 60 + Number(val.ss);
        this.$emit('input', this.value);
      }
    },
    updateValues() {
      let newTimeValue;
      if (!this.valueDuration.isValid() || this.valueDuration.asSeconds() < 1) {
        // Minimum threshold (< 1 second) or invalidity
        newTimeValue = { HH: '00', mm: '00', ss: '05' };
      } else if (this.valueDuration.asHours() >= 24) {
        // Maximum threshold (> 23h59m59s)
        newTimeValue = { HH: '23', mm: '59', ss: '59' };
      } else {
        newTimeValue = {
          HH: (this.valueDuration.hours() < 10 ? '0' : '') + String(this.valueDuration.hours()),
          mm: (this.valueDuration.minutes() < 10 ? '0' : '') + String(this.valueDuration.minutes()),
          ss: (this.valueDuration.seconds() < 10 ? '0' : '') + String(this.valueDuration.seconds()),
        };
      }
      this.timeValue = newTimeValue;
    },
    confirmDelete() {
      calert(this.$t('message.CONFIRM_REMOVE_STEP'),
        [
          {
            title: this.$t('message.REMOVE'),
            action: () => {
              this.$emit('removeStep');
            },
            sClass: 'red',
          },
          { title: this.$t('message.CANCEL') },
        ]);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.meeting-step {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  margin-top: 0px;
  padding: 1rem 0.5rem 1rem 1.5rem;
  display: flex;

  &.generic-border--thin-border {
    padding-left: 1.8rem;
    padding-right: 1rem;
  }

  .dragging-indication {
    position: absolute;
    left: .5rem;
    top: 50%;
    transform: translateY(-50%) scale(0.8);
  }

  &__details {
    flex: 1;
    min-width: 0px;

    &__basics {
      .editable-label {
        flex: 1;
        min-width: 0px;

        &:first-child {
          margin-right: 0.4rem;
        }

        &:last-child {
          margin-left: 0.4rem;
        }
      }
    }

    .page-picker {
      margin-top: 0.5rem;
    }
  }

  &__duration {
    flex: 0 0 auto;
    margin-left: 1rem;
    display: flex;
    flex-flow: column;
    align-items: flex-end;

    &__content {
      flex: 1;
      min-height: 0px;
    }

    &__hint {
      margin-bottom: 0.7rem;
    }
  }


  &.ui-sortable-helper {
    background: $basic-section-background-transparent;
  }
}
</style>
